import {
  FiAlignLeft,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiMoreHorizontal,
  FiPlay,
  FiRotateCcw,
  FiSearch,
  FiTerminal,
} from "react-icons/fi";
import { useEffect, useRef, useState } from "react";

const ScriptEditorToolbar = ({
  isExecuting,
  isPreviewing,
  draftSaveStatus = "idle",
  isLoadingScript,
  totalVersions,
  currentPage,
  hasPrev,
  hasNext,
  onLoadOlder,
  onLoadNewer,
  onReset,
  onPreview,
  previewSummaryMode = "none",
  onChangePreviewSummaryMode,
  onFormat,
  onRun,
  onOpenFind,
  showConsole,
  onToggleConsole,
}) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const canInteract = !isExecuting && !isLoadingScript;
  const currentVersion =
    totalVersions > 0 ? totalVersions - currentPage + 1 : 0;
  const draftStatusLabel =
    draftSaveStatus === "saving"
      ? "Saving draft..."
      : draftSaveStatus === "saved"
        ? "Draft saved"
        : draftSaveStatus === "error"
          ? "Draft save failed"
          : "Draft idle";

  useEffect(() => {
    if (!showMoreMenu) return undefined;

    const handlePointerDown = (event) => {
      if (!moreMenuRef.current?.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMoreMenu]);

  return (
    <div className="animation-workbench-panel-head">
      <h3>Script Editor</h3>
      <div className="animation-workbench-panel-actions">
        <span
          className={`animation-workbench-draft-status is-${draftSaveStatus}`}
          title={draftStatusLabel}
        >
          {draftStatusLabel}
        </span>
        <span className="animation-workbench-version">
          {currentVersion} / {totalVersions}
        </span>
        <button
          type="button"
          className="animation-workbench-btn animation-workbench-preview-btn"
          onClick={onPreview}
          disabled={!canInteract || isPreviewing}
          title={isPreviewing ? "Previewing..." : "Preview script"}
          aria-label={isPreviewing ? "Previewing script" : "Preview script"}
        >
          <FiEye />
        </button>
        <button
          type="button"
          className="animation-workbench-btn animation-workbench-run-btn"
          onClick={onRun}
          disabled={!canInteract}
          title={isExecuting ? "Running..." : "Run script"}
          aria-label={isExecuting ? "Running script" : "Run script"}
        >
          <FiPlay />
        </button>
        <div className="animation-workbench-toolbar-menu" ref={moreMenuRef}>
          <button
            type="button"
            className={`animation-workbench-btn ${showMoreMenu ? "active" : ""}`}
            onClick={() => setShowMoreMenu((current) => !current)}
            title="More options"
            aria-label="More options"
            aria-expanded={showMoreMenu}
          >
            <FiMoreHorizontal />
          </button>
          {showMoreMenu && (
            <div className="animation-workbench-toolbar-popover">
              <div className="animation-workbench-toolbar-popover-group">
                <span className="animation-workbench-toolbar-popover-label">
                  Versions
                </span>
                <div className="animation-workbench-toolbar-inline">
                  <button
                    type="button"
                    className="animation-workbench-btn"
                    onClick={() => {
                      onLoadOlder?.();
                      setShowMoreMenu(false);
                    }}
                    disabled={!hasPrev || !canInteract}
                    title="Older version"
                  >
                    <FiChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="animation-workbench-btn"
                    onClick={() => {
                      onLoadNewer?.();
                      setShowMoreMenu(false);
                    }}
                    disabled={!hasNext || !canInteract}
                    title="Newer version"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
              <div className="animation-workbench-toolbar-popover-group">
                <span className="animation-workbench-toolbar-popover-label">
                  Preview summary
                </span>
                <label className="animation-workbench-preview-select-wrap">
                  <select
                    className="animation-workbench-preview-select"
                    value={previewSummaryMode}
                    onChange={(event) =>
                      onChangePreviewSummaryMode?.(event.target.value)
                    }
                    disabled={!canInteract}
                    aria-label="Preview summary mode"
                  >
                    <option value="none">No summary</option>
                    <option value="short">Short</option>
                    <option value="long">Long</option>
                  </select>
                </label>
              </div>
              <button
                type="button"
                className="animation-workbench-toolbar-popover-action"
                onClick={() => {
                  onFormat?.();
                  setShowMoreMenu(false);
                }}
                disabled={!canInteract}
              >
                <FiAlignLeft />
                <span>Format script</span>
              </button>
              <button
                type="button"
                className="animation-workbench-toolbar-popover-action"
                onClick={() => {
                  onOpenFind?.();
                  setShowMoreMenu(false);
                }}
                disabled={!canInteract}
              >
                <FiSearch />
                <span>Find / Replace</span>
              </button>
              <button
                type="button"
                className="animation-workbench-toolbar-popover-action"
                onClick={() => {
                  onReset?.();
                  setShowMoreMenu(false);
                }}
                disabled={!canInteract}
              >
                <FiRotateCcw />
                <span>Reset script</span>
              </button>
              <button
                type="button"
                className={`animation-workbench-toolbar-popover-action${
                  showConsole ? " active" : ""
                }`}
                onClick={() => {
                  onToggleConsole?.();
                  setShowMoreMenu(false);
                }}
              >
                <FiTerminal />
                <span>{showConsole ? "Hide console" : "Show console"}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptEditorToolbar;
